import { useState } from "react";
import { toast } from "sonner";
import { fmtCFA, fmtDate } from "@/lib/format";
import { imprimerRecu } from "@/lib/printRecu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Printer, Zap, HelpCircle } from "lucide-react";
import { useLogo } from "@/context/LogoContext";
import { Switch } from "@/components/ui/switch";
import { LOGO_BW_SVG, getAutoPrint, setAutoPrint } from "@/lib/recuPrefs";

const LABELS = { especes: "Espèces", wave: "Wave", orange_money: "Orange Money" };
const FORMAT_KEY = "gym_recu_format";

function TicketLogo({ logoUrl, a4 }) {
  const cls = `${a4 ? "w-24 h-24" : "w-14 h-14"} object-contain mx-auto mb-1`;
  if (logoUrl) return <img src={logoUrl} alt="Logo" className={`${cls} grayscale contrast-150`} data-testid="recu-logo" />;
  return <div className={cls} data-testid="recu-logo-fallback" dangerouslySetInnerHTML={{ __html: LOGO_BW_SVG.replace('width="56" height="56"', 'width="100%" height="100%"') }} />;
}

export function GuideImpressionDirecte() {
  return (
    <details className="rounded-lg border border-border bg-background/60 p-3 text-xs text-foreground/80" data-testid="guide-impression-directe">
      <summary className="cursor-pointer font-medium flex items-center gap-1.5"><HelpCircle className="w-3.5 h-3.5" /> Imprimer sans la boîte de dialogue Chrome (Xprinter)</summary>
      <ol className="list-decimal pl-4 mt-2 space-y-1">
        <li>Dans Windows : <strong>Paramètres → Imprimantes</strong>, définir <strong>Xprinter</strong> comme imprimante par défaut (format papier 80 mm).</li>
        <li>Clic droit sur le raccourci Chrome → <strong>Propriétés</strong> → champ <em>Cible</em>, ajouter à la fin :<br /><code className="bg-muted px-1 rounded">--kiosk-printing</code></li>
        <li>Fermer complètement Chrome, puis le relancer depuis ce raccourci.</li>
        <li>Activer ci-dessus « Impression automatique » : chaque vente validée part directement sur la Xprinter, en un clic.</li>
      </ol>
    </details>
  );
}

function Ticket({ vente, logoUrl, format }) {
  const a4 = format === "a4";
  return (
    <div className={`recu-copy bg-white text-black ${a4 ? "p-10 text-sm max-w-[520px] mx-auto space-y-3" : "p-3 text-[11px] leading-tight space-y-1.5"} font-mono`}>
      <div className="text-center space-y-0.5">
        <TicketLogo logoUrl={logoUrl} a4={a4} />
        <p className={`font-bold tracking-wide ${a4 ? "text-2xl" : "text-base"}`}>LAC ROSE FITNESS</p>
        <p>Reçu de caisse</p>
        <p>{fmtDate(vente.created_at)}</p>
        <p>N° {vente.id.slice(0, 8).toUpperCase()}</p>
      </div>
      <hr className="border-dashed border-black" />
      <table className="w-full">
        <tbody>
          {vente.items.map((i) => (
            <tr key={`${i.type}-${i.ref_id || i.nom}-${i.membre_id || ""}`}>
              <td className="py-0.5 pr-2">{i.quantite}× {i.nom}</td>
              <td className="py-0.5 text-right whitespace-nowrap">{fmtCFA(i.prix * i.quantite)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <hr className="border-dashed border-black" />
      <div className={`flex justify-between font-bold ${a4 ? "text-lg" : "text-sm"}`}>
        <span>TOTAL</span>
        <span data-testid="recu-total">{fmtCFA(vente.total)}</span>
      </div>
      <div className="flex justify-between"><span>Paiement</span><span data-testid="recu-paiement">{LABELS[vente.paiement] || vente.paiement}</span></div>
      {vente.reference && <div className="flex justify-between"><span>Réf. transaction</span><span data-testid="recu-reference">{vente.reference}</span></div>}
      <div className="flex justify-between"><span>Vendeur</span><span>{vente.vendeur_nom}</span></div>
      <hr className="border-dashed border-black" />
      <p className="text-center">Merci de votre visite !</p>
    </div>
  );
}

export default function Recu({ vente, onClose }) {
  const { logoUrl } = useLogo();
  const [format, setFormat] = useState(() => localStorage.getItem(FORMAT_KEY) || "ticket");
  const [copies, setCopies] = useState(1);
  const [auto, setAuto] = useState(getAutoPrint);
  if (!vente) return null;

  const changeFormat = (f) => { setFormat(f); localStorage.setItem(FORMAT_KEY, f); };
  const changeAuto = (v) => { setAuto(v); setAutoPrint(v); toast.success(v ? "Impression automatique activée : le reçu part dès la validation de la vente" : "Impression automatique désactivée"); };
  const nb = Math.min(10, Math.max(1, Number(copies) || 1));
  const imprimer = () => {
    if (!imprimerRecu(vente, { logoUrl, format, copies: nb })) toast.error("Fenêtre d'impression bloquée : autorisez les pop-ups pour ce site");
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card border-border text-foreground max-w-md max-h-[92vh] overflow-y-auto" data-testid="recu-dialog">
        <DialogHeader className="print:hidden">
          <DialogTitle>Reçu de vente</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 print:hidden">
          <div className="space-y-1.5">
            <Label className="text-foreground/80 text-xs">Format</Label>
            <Select value={format} onValueChange={changeFormat}>
              <SelectTrigger data-testid="recu-format-select" className="bg-background border-border text-foreground h-9"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border text-foreground">
                <SelectItem value="a4">Page normale (A4)</SelectItem>
                <SelectItem value="ticket">Ticket 80 mm</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-foreground/80 text-xs">Exemplaires</Label>
            <Input type="number" min="1" max="10" value={copies} onChange={(e) => setCopies(e.target.value)} data-testid="recu-copies-input" className="bg-background border-border text-foreground h-9" />
          </div>
        </div>
        <div className={`rounded-lg overflow-hidden border border-border ${format === "ticket" ? "max-w-[80mm] mx-auto" : ""}`} data-testid="recu-content" data-copies={nb}>
          <Ticket vente={vente} logoUrl={logoUrl} format={format} />
        </div>
        <p className="text-[11px] text-muted-foreground print:hidden">Aperçu. L'impression s'ouvre dans une fenêtre dédiée au format {format === "ticket" ? "ticket 80 mm" : "A4"} (sans page vide).</p>
        <div className="flex items-center justify-between rounded-lg border border-border bg-background/60 px-3 py-2">
          <Label htmlFor="recu-auto" className="text-xs text-foreground/80 flex items-center gap-1.5 cursor-pointer"><Zap className="w-3.5 h-3.5 text-amber-500" /> Impression automatique à la validation</Label>
          <Switch id="recu-auto" checked={auto} onCheckedChange={changeAuto} data-testid="recu-auto-switch" />
        </div>
        <GuideImpressionDirecte />
        <DialogFooter className="print:hidden">
          <Button variant="outline" onClick={onClose} className="border-border text-foreground/80" data-testid="recu-close-button">Fermer</Button>
          <Button onClick={imprimer} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="recu-print-button">
            <Printer className="w-4 h-4 mr-2" /> Imprimer {nb > 1 ? `(${nb} ex.)` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
